import {useEffect} from 'react';

import {isEmpty, isNil} from 'ramda';

import {omneticApi} from '@dms/api/base';

import {AppSyncMessage, SubscribeToNotificationsApiArg} from './types';
import {buildWebSocketUrl} from './utils/buildWebSocketUrl';
import {captureWebsocketError} from './utils/captureWebsocketError';
import {createSubscriptionMessage} from './utils/createSubscriptionMessage';
import {getSubscriptionId} from './utils/getSubscriptionId';
import {handleIncomingData} from './utils/handleIncomingData';
import {handleWebSocketMessage} from './utils/handleWebSocketMessage';

const WEBSOCKET_PROTOCOL = 'graphql-ws';
const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;
const MAX_CACHE_SIZE = 50;
const RECONNECT_DELAY_MS = 5_000;
const NORMAL_CLOSE_CODE = 1000;

export const appSyncApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    subscribeToNotifications: build.query<SubscribeToNotificationsApiArg[], void>({
      queryFn: () => ({
        data: [],
      }),
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(args, {cacheEntryRemoved, getState, dispatch, updateCachedData}) {
        let ws: WebSocket | null = null;
        let keepAliveTimeoutMs = FIVE_MINUTES_IN_MS;
        let keepAliveTimeout: ReturnType<typeof setTimeout> | undefined;
        let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
        let isCleaningUp = false;

        const scheduleReconnect = () => {
          if (isCleaningUp) {
            return;
          }
          clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
        };

        const connect = () => {
          if (isCleaningUp) {
            return;
          }

          const url = buildWebSocketUrl();
          if (isNil(url)) {
            scheduleReconnect();
            return;
          }

          try {
            ws = new WebSocket(url, [WEBSOCKET_PROTOCOL]);
          } catch (error) {
            captureWebsocketError(error);
            scheduleReconnect();
            return;
          }

          const subscriptionId = getSubscriptionId(getState());

          const resetKeepAlive = () => {
            clearTimeout(keepAliveTimeout);
            keepAliveTimeout = setTimeout(() => {
              if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) {
                ws.close();
              }
            }, keepAliveTimeoutMs);
          };

          ws.onopen = () => ws?.send(JSON.stringify({type: 'connection_init'}));
          ws.onerror = captureWebsocketError;
          ws.onclose = (event) => {
            keepAliveTimeoutMs = FIVE_MINUTES_IN_MS;
            clearTimeout(keepAliveTimeout);
            if (isCleaningUp || event.code === NORMAL_CLOSE_CODE) {
              return;
            }
            scheduleReconnect();
          };
          ws.onmessage = (event) => {
            try {
              const message = JSON.parse(event.data) as AppSyncMessage;

              handleWebSocketMessage(message, {
                onConnectionAck: (keepAliveMs) => {
                  const subscriptionMessage = createSubscriptionMessage(subscriptionId);

                  if (isNil(subscriptionMessage)) {
                    return;
                  }

                  ws?.send(JSON.stringify(subscriptionMessage));
                  keepAliveTimeoutMs = keepAliveMs;
                  resetKeepAlive();
                },
                onData: handleIncomingData(dispatch, (notification) =>
                  updateCachedData((draft) => {
                    if (isNil(draft)) {
                      return [notification];
                    }

                    if (draft.length >= MAX_CACHE_SIZE) {
                      return [...draft.slice(1), notification];
                    }

                    return [...draft, notification];
                  })
                ),
                onError: captureWebsocketError,
                onComplete: () => ws?.close(),
                onKeepAlive: resetKeepAlive,
              });
            } catch (error) {
              captureWebsocketError(error);
            }
          };
        };

        connect();

        // is "pending" until the hook is unmounted
        await cacheEntryRemoved;

        const closeSocket = (socket: WebSocket | null) => {
          if (isNil(socket)) {
            return;
          }

          if (socket.readyState === WebSocket.OPEN) {
            const subscriptionId = getSubscriptionId(getState());
            socket.send(
              JSON.stringify({
                id: subscriptionId,
                type: 'stop',
              })
            );
          }

          socket.close(NORMAL_CLOSE_CODE);
        };

        isCleaningUp = true;
        clearTimeout(reconnectTimer);
        clearTimeout(keepAliveTimeout);

        closeSocket(ws);
      },
    }),
  }),
});

export const useSubscribeToNotificationsQuery = (
  onData: (notification: SubscribeToNotificationsApiArg) => void
) => {
  const {data: latestData} = appSyncApi.useSubscribeToNotificationsQuery();

  useEffect(() => {
    if (isNil(latestData) || isEmpty(latestData)) {
      return;
    }

    onData(latestData[latestData.length - 1]);
  }, [latestData]);

  return [latestData];
};
